import { BackButton } from "@publicPortal/components/BackButton/BackButton";
import EntityTable from "@mirage/components/entityTable/EntityTable";
import { Box, IconButton, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import Board from "@publicPortal/components/board/Board";
import ResponsiveAppBar from "@publicPortal/components/responsiveAppBar/ResponsiveAppBar";
import WeeklyListsService from "@publicPortal/services/WeeklyListsService";
import { Link } from "react-router-dom";
import React, { useCallback } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { GridRenderCellParams } from "@mui/x-data-grid-premium";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel, faFileWord, faFilePdf, faFileCsv } from "@fortawesome/pro-regular-svg-icons";
import { PublicPortalTheme } from "@publicPortal/theme/MuiPublicPortal.module";
import LocalAttachmentsService from "@mirage/services/LocalAttachmentsService";
import { IEntityPageRequest } from "@shared/mirage/core/contracts/IEntityPageRequest";
import { Filter, FilterItem } from "@shared/mirage/entityTable/Filter";
import DateFormatter from "@shared/mirage/helpers/DateFormatter";
import { PublicPortalRoutingService } from "@publicPortal/services/PublicPortalRoutingService";
dayjs.extend(utc);
const weeklyListsService = new WeeklyListsService();

export const WeeklyListsSearchResult: React.FC = () => {
    const attachmentService = new LocalAttachmentsService();
    const pp = new PublicPortalRoutingService();
    const onPageRequest = useCallback(async (pageRequest: IEntityPageRequest) => {
        const weeklyLists = await weeklyListsService.pageRequest(pageRequest);
        return weeklyLists;
    }, []);

    const getFilter = (): Filter => {
        const filter = { parts: [] } as Filter;
        const filterItems: FilterItem[] = [];
        const sp = new URL(window.location.href).searchParams;
        const params = Array.from(sp);

        params.forEach(([key, value]) => {
            const lowercaseKey = key.toLowerCase();

            if (lowercaseKey === "datecreatedfrom") {
                filterItems.push({ columnName: "dateCreatedFrom", value, operator: ">" });
            } else if (lowercaseKey === "datecreatedto") {
                filterItems.push({ columnName: "dateCreatedTo", value, operator: "<" });
            }
        });

        filter.parts.push({ filterItems: filterItems });
        return filter;
    };

    function getColumns(): GridColDef[] {
        const dateCreatedCell = (params: GridRenderCellParams) => {
            return <Link to={getRecordDetailsUrl(params.formattedValue)}>{params.formattedValue}</Link>;
        };

        const downloadCell = (params: GridRenderCellParams) => {
            async function downloadFile(fileId: string) {
                const result = await attachmentService.DownloadAttachment(Number(fileId));

                if (result === null) {
                    alert("File not found");
                } else {
                    window.location.assign(result.url);
                }
            }

            return (
                <>
                    <Tooltip title={"Excel"}>
                        <IconButton onClick={() => downloadFile(params.row.excelId)}>
                            <FontAwesomeIcon icon={faFileExcel} style={PublicPortalTheme.icons.sizeM} color="#1D6F42" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={"Word"}>
                        <IconButton onClick={() => downloadFile(params.row.wordId)}>
                            <FontAwesomeIcon icon={faFileWord} style={PublicPortalTheme.icons.sizeM} color="#1B5EBE" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={"CSV"}>
                        <IconButton onClick={() => downloadFile(params.row.textId)}>
                            <FontAwesomeIcon icon={faFileCsv} style={PublicPortalTheme.icons.sizeM} color="#4f4f4f" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={"PDF"}>
                        <IconButton onClick={() => downloadFile(params.row.pdfId)}>
                            <FontAwesomeIcon icon={faFilePdf} style={PublicPortalTheme.icons.sizeM} color="#F40F02" />
                        </IconButton>
                    </Tooltip>
                </>
            );
        };

        return [
            { field: "dateCreated", headerName: "Date", type: "date", renderCell: dateCreatedCell },
            { field: "name", headerName: "Name" },
            { field: "download", headerName: "Download", renderCell: downloadCell, sortable: false },
        ];
    }

    function getRecordDetailsUrl(dateCreated: Date) {
        const dateCreatedFormatted = DateFormatter.formatToDate(dateCreated);

        return `${pp.PlanningApplicationsRoute()}/?weeklyListDate=${dateCreatedFormatted}`;
    }

    return (
        <>
            <ResponsiveAppBar />
            <Board size="big" headerText="Weekly Lists">
                <BackButton />
                <Box sx={{ marginTop: "32px", height: "auto" }}>
                    <EntityTable title="" key="weekly-list-entityTable" stateKey="weeklyLists" onPageRequest={onPageRequest} columns={getColumns()} filter={getFilter()} />
                </Box>
            </Board>
        </>
    );
};
